import browserImageSize from 'browser-image-size';
import { Component, ReactElement, ReactNode, RefObject, createRef } from 'react';
import Dropzone from 'react-dropzone';
import { Trans, getI18n } from 'react-i18next';
import { S3Response } from 'react-s3-uploader';
import S3Upload from 'react-s3-uploader/s3upload';
import type { IIconProps, IS3File } from '@bridebook/ui/src/types';
import Box from '../fela/Box';
import { uploadToCloudflareImagesFromUrl } from './cloudflare-utils';
import ErrorFileSize from './error-file-size';
import ErrorFileType from './error-file-type';
import ErrorImageSize from './error-image-size';
import ErrorReachedLimit from './error-reached-limit';
import componentStyles, { FileUploaderStyles } from './file-uploader.style';
import { CustomFileError, DroppedFile, DroppedPromise } from './types';
import UploadingFile from './uploading-file';
import { checkFileType, createDroppedPromise } from './utils';
import { BUCKETS } from '@bridebook/toolbox/src/api/r2/config';
import { R2_UPLOAD_PATH } from '@bridebook/toolbox/src/api/r2/sign';
import { normalizeFileExtensions } from '@bridebook/toolbox/src/api/cloudflare-images/normalize-file-extension';
import { Icon, Text } from '@bridebook/aura';
import { Plus } from '@bridebook/aura/src/icons';

interface SigningUrlQueryParams {
  directory?: string;
  bucket: string;
  prefix?: string;
  region?: string;
}

interface UploadError {
  errorMsgBlock: ReactNode;
  errorMsg: string;
  failReason?: string;
}

export interface IProps {
  onProgress?: (percents: number, status: string, file: File) => void;
  onUploadFinish?: (s3file: IS3File, file: File) => void;
  onMultipleUploadFinish?: (files: IS3File[]) => void;
  onFileAddErrorAnalytics?: (...args: any) => void;
  directory?: string;
  multiple?: boolean;
  uploadType?: 'photo' | 'file' | '*';
  uploadLabel?: string | ReactElement;
  maxFileSize: number;
  acceptFileTypes?: Array<string>;
  minImageWidth?: number;
  onFileAddError?: (msg: string) => void;
  allowUpload?: boolean;
  style?: FileUploaderStyles;
  preprocess?: (file: File, next: (file: File) => unknown) => void;
  preprocessOverride?: (file: File, next: (file: File) => unknown) => any;
  bucket?: string;
  accountId?: string;
  prefix?: string;
  children?: any;
  region?: string;
  autostart?: boolean;
  setRef?: RefObject<any>;
  allowTranslation?: boolean;
  disablePreview?: boolean;
  isMobile?: boolean;
  disabled?: boolean;
  checkRemainingFileSlots?: () => number;
  iconColor?: IIconProps['color'];
  showUploadInfo?: boolean;
  enableCloudflareImages?: boolean;
}

interface IState {
  droppedFiles: DroppedFile[];
  errors: CustomFileError[];
  allDroppedPromise: DroppedPromise;
}

class FileUploader extends Component<IProps, IState> {
  resetTimeout: any;
  dropzoneRef: RefObject<Dropzone>;
  // Maps any File reference (original or preprocess-replaced) to its DroppedFile entry,
  // so identity lookups still resolve when a consumer preprocess returns a new File.
  private fileToDropped = new Map<File, DroppedFile>();

  constructor(props: IProps) {
    super(props);

    this.dropzoneRef = props.setRef || createRef();
    this.resetTimeout = null;
    this.state = {
      droppedFiles: [],
      errors: [],
      allDroppedPromise: createDroppedPromise(),
    };
  }

  componentDidMount() {
    if (this.props.autostart && this.dropzoneRef.current) {
      this.dropzoneRef.current.open();
    }
  }

  onProgress = (percents: number, status: string, file: File): void => {
    // clear resetting timeout in case it's initiated
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
      this.resetTimeout = null;
    }

    const { droppedFiles } = this.state;
    const newState = droppedFiles.map((dropped) => {
      if (dropped.file === file) return { ...dropped, percents, status };
      return dropped;
    });
    this.setState({ droppedFiles: newState });
  };

  onDrop = async (acceptedFiles: Array<File>, rejectedFiles: Array<File>): Promise<void> => {
    const {
      acceptFileTypes,
      onFileAddErrorAnalytics,
      directory,
      bucket = BUCKETS.media,
      prefix,
      onProgress,
      preprocessOverride,
      region = '',
      allowTranslation,
      checkRemainingFileSlots,
    } = this.props;

    const remainingFileSlots = checkRemainingFileSlots
      ? checkRemainingFileSlots()
      : acceptedFiles.length;

    const acceptedFilesToUpload = acceptedFiles.slice(0, remainingFileSlots);
    const filesAboveLimit = acceptedFiles.slice(remainingFileSlots, acceptedFiles.length);

    const filesToUpload = normalizeFileExtensions(acceptedFilesToUpload);

    const signingUrlQueryParams: SigningUrlQueryParams = { directory, bucket, region };

    if (prefix) {
      signingUrlQueryParams.prefix = prefix;
    }

    const uploaderProps = {
      s3Url: R2_UPLOAD_PATH,
      signingUrlQueryParams,
      signingUrl: '/api/r2/sign',
      // Sad but true, this is necessary to not let S3Uploader
      // break SigV4 signatures.
      uploadRequestHeaders: {
        'X-By': 'BB',
      },
    };

    // @ts-ignore
    new S3Upload({
      files: filesToUpload,
      ...uploaderProps,
      preprocess: preprocessOverride || this.preprocess,
      onFinishS3Put: this.handleFinishedUpload,
      onProgress: onProgress || this.onProgress,
      onError: this.onError,
    });

    const droppedFiles = [...filesToUpload, ...filesAboveLimit, ...rejectedFiles].map((file) => {
      const isRejected = rejectedFiles.includes(file);
      const isAboveLimit = filesAboveLimit.includes(file);

      let analyticsErrorMsg = '';

      const dropped: DroppedFile = {
        file,
        promise: createDroppedPromise(),
      };

      this.fileToDropped.set(file, dropped);

      if (isRejected) {
        dropped.msg = (
          <ErrorFileType
            fileName={file.name}
            acceptFileTypes={acceptFileTypes}
            allowTranslation={allowTranslation}
          />
        );
        analyticsErrorMsg = 'Wrong file type';
      }

      if (isAboveLimit) {
        dropped.msg = (
          <ErrorReachedLimit fileName={file.name} allowTranslation={allowTranslation} />
        );
        analyticsErrorMsg = 'Files upload limit reached';
      }

      if (isRejected || isAboveLimit) {
        onFileAddErrorAnalytics?.(analyticsErrorMsg);
        dropped.promise.resolve(undefined);
      }

      return dropped;
    });
    const promises = droppedFiles.map((dropped) => dropped.promise.promise);

    if (!promises.length) {
      const failReason = 'wrongFileType';
      const file = {
        type: '',
        size: 0,
        name: '',
      };
      const errorMsgBlock = (
        <ErrorFileType
          fileName={file.name}
          acceptFileTypes={acceptFileTypes}
          allowTranslation={allowTranslation}
        />
      );
      const errorMsg = `Wrong file type ${file.type}`;
      const metadata = {
        'Content-Length': file.size,
        filename: file.name,
      };

      this.handleUploadError(file as File, errorMsgBlock, failReason);
      onFileAddErrorAnalytics?.(errorMsg, metadata, file);
    }

    Promise.all(promises).then((data) => {
      const { onMultipleUploadFinish } = this.props;
      if (onMultipleUploadFinish) {
        const filesUploaded = data.filter((d) => d !== null);
        if (filesUploaded.length) {
          onMultipleUploadFinish(filesUploaded);
        }
      }
    });
    this.setState({ droppedFiles });
    this.state.allDroppedPromise.resolve();
  };

  onError = (error: string): void => {
    this.props.onFileAddErrorAnalytics?.(error, {});
    this.resetErrorMsg();
  };

  resetErrorMsg = (): void => {
    // clear previous timeout
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
      this.resetTimeout = null;
    }
    this.resetTimeout = setTimeout(() => {
      this.setState({ errors: [], droppedFiles: [] });
    }, 10000);
  };

  clearDroppedFiles = (file: File) => {
    if (file) {
      const dropped = this.fileToDropped.get(file);
      this.fileToDropped.delete(file);
      if (dropped) {
        this.setState({
          droppedFiles: this.state.droppedFiles.filter((d) => d !== dropped),
        });
      }
    } else {
      this.fileToDropped.clear();
      this.setState({ droppedFiles: [] });
    }
  };

  handleFinishedUpload = async (s3response: S3Response, file: File): Promise<void> => {
    this.state.allDroppedPromise.promise.then(async () => {
      const dropped = this.fileToDropped.get(file);
      const s3file: IS3File = {
        ...s3response,
        size: file.size,
      };

      if (this.props.enableCloudflareImages) {
        try {
          console.log('🔄 Uploading to Cloudflare Images from R2 URL');
          await uploadToCloudflareImagesFromUrl(s3file, BUCKETS.media);
          console.log('✅ Successfully uploaded to both R2 and Cloudflare Images');
        } catch (error) {
          console.error('❌ Cloudflare Images upload failed:', error);
        }
      }

      if (dropped) dropped.promise.resolve(s3file);
      this.clearDroppedFiles(file);
      const { onUploadFinish } = this.props;

      if (onUploadFinish) {
        onUploadFinish(s3file, file);
      }

      this.resetErrorMsg();
    });
  };

  handleUploadError = (file: File, msg: ReactNode | string | null, reason: string = ''): void => {
    const { onFileAddError } = this.props;
    this.state.allDroppedPromise.promise.then(() => {
      const dropped = this.fileToDropped.get(file);
      const error: CustomFileError = { file, msg };
      const { errors } = this.state;

      errors.push(error);
      if (dropped) dropped.promise.resolve(undefined);
      this.setState({ errors });
      if (onFileAddError) {
        onFileAddError(reason);
      }
      this.forceUpdate();
    });
  };

  stopPendingUploads = (): void => {
    const { allowTranslation } = this.props;
    // kill all pending uploads if limit breached
    this.state.allDroppedPromise.promise.then(() => {
      this.state.droppedFiles.forEach((dropped) => {
        dropped.promise.resolve(undefined);
        const { file } = dropped;
        const errorBlock = (
          <ErrorReachedLimit fileName={file.name} allowTranslation={allowTranslation} />
        );
        this.handleUploadError(file, errorBlock, 'reachedLimit');
      });
    });
  };

  private validateUploadSize = (file: File): UploadError | undefined => {
    const { maxFileSize, allowTranslation } = this.props;
    const sizeCorrect = file.size <= 1024 * 1024 * maxFileSize;

    if (!sizeCorrect) {
      return {
        errorMsgBlock: (
          <ErrorFileSize
            fileName={file.name}
            maxFileSize={maxFileSize}
            allowTranslation={allowTranslation}
          />
        ),
        errorMsg: 'Wrong file size',
        failReason: 'wrongFileSize',
      };
    }

    return;
  };

  private validateUploadType = async (file: File): Promise<UploadError | undefined> => {
    const { acceptFileTypes, allowTranslation } = this.props;
    if (!acceptFileTypes) {
      const validFileType = await checkFileType(file, acceptFileTypes);
      return (
        !validFileType && {
          failReason: 'wrongFileType',
          errorMsgBlock: (
            <ErrorFileType
              fileName={file.name}
              acceptFileTypes={acceptFileTypes}
              allowTranslation={allowTranslation}
            />
          ),
          errorMsg: `Wrong file type ${file.type}`,
        }
      );
    }

    return;
  };

  private validateImageWidth = (file: File, width: number): UploadError | undefined => {
    const { minImageWidth, allowTranslation } = this.props;
    if (width < minImageWidth) {
      return {
        errorMsgBlock: (
          <ErrorImageSize
            fileName={file.name}
            minImageWidth={minImageWidth}
            allowTranslation={allowTranslation}
          />
        ),
        errorMsg: 'Wrong image size',
      };
    }

    return;
  };

  private validateImageSize =
    (file: File) =>
    ({ width, height }: { width: number; height: number }) => {
      const sizeError = this.validateUploadSize(file);
      const imageWidthError = this.validateImageWidth(file, width);
      const error = sizeError || imageWidthError;

      return { error, width, height };
    };

  private handleImageValidationError = (
    file: File,
    error: UploadError,
    width: number,
    height: number,
  ) => {
    const { onFileAddErrorAnalytics } = this.props;
    const metadata = { 'Content-Length': file.size, filename: file.name };

    this.handleUploadError(file, error.errorMsgBlock);
    onFileAddErrorAnalytics?.(
      error.errorMsg,
      {
        ...metadata,
        PixelWidth: width,
        PixelHeight: height,
      },
      file,
    );
  };

  private preprocessImage = (
    file: File,
    next: (file: File) => unknown,
  ): Promise<UploadError | void> => {
    const { preprocess } = this.props;
    return browserImageSize(file)
      .then(this.validateImageSize(file))
      .then(({ width, height, error }) => {
        if (error) {
          this.handleImageValidationError(file, error, width, height);
          return error;
        }

        preprocess instanceof Function ? preprocess(file, next) : next(file);
      });
  };

  preprocess = async (file: File, next: (file: File) => unknown): Promise<any> => {
    const {
      onFileAddErrorAnalytics,
      acceptFileTypes,
      allowUpload = true,
      uploadType = 'photo',
      preprocess,
      allowTranslation,
    } = this.props;
    // If a consumer preprocess returns a new File, handleFinishedUpload would get that new
    // File from S3Upload and fail to match the original in fileToDropped — the per-file
    // promise would never resolve and onMultipleUploadFinish would never fire. Re-key the
    // dropped entry under the processed file to keep the lookup working.
    next = ((originalNext) => (processedFile: File) => {
      if (processedFile !== file) {
        const dropped = this.fileToDropped.get(file);
        if (dropped) this.fileToDropped.set(processedFile, dropped);
      }
      originalNext(processedFile);
    })(next);
    const metadata = { 'Content-Length': file.size, filename: file.name };
    // mostly when files upload limit is reached
    if (!allowUpload) {
      onFileAddErrorAnalytics?.('Files upload limit reached', metadata, file);
      this.stopPendingUploads();
      return;
    }
    if (uploadType === 'photo') {
      this.preprocessImage(file, next).catch(() => {
        onFileAddErrorAnalytics?.(`Wrong file type ${file.type}`, metadata);
        this.handleUploadError(
          file,
          <ErrorFileType
            fileName={file.name}
            acceptFileTypes={acceptFileTypes}
            allowTranslation={allowTranslation}
          />,
        );
      });
    } else if (uploadType === 'file') {
      const sizeError = this.validateUploadSize(file);
      const fileTypeError = await this.validateUploadType(file);
      const error = sizeError || fileTypeError;

      if (error) {
        this.handleUploadError(file, error.errorMsgBlock, error.errorMsg);
        onFileAddErrorAnalytics?.(error.errorMsg, metadata, file);
        return;
      }

      preprocess instanceof Function ? preprocess(file, next) : next(file);
    } else if (uploadType === '*') {
      // First try to preprocess file as image to avoid extension checking
      try {
        await this.preprocessImage(file, next);
      } catch (e) {
        const sizeError = this.validateUploadSize(file);
        const fileTypeError = await this.validateUploadType(file);
        const error = sizeError || fileTypeError;

        if (error) {
          this.handleUploadError(file, error.errorMsgBlock, error.errorMsg);
          onFileAddErrorAnalytics?.(error.errorMsg, metadata, file);
          return;
        }

        preprocess instanceof Function ? preprocess(file, next) : next(file);
      }
    }
  };

  render() {
    const {
      multiple = true,
      uploadLabel,
      uploadType = 'photo',
      acceptFileTypes,
      style,
      children,
      disablePreview,
      disabled,
      allowUpload = true,
      showUploadInfo,
    } = this.props;
    const { droppedFiles, errors } = this.state;

    const styles = style || componentStyles();

    return (
      <Box style={styles.fileUploader}>
        <Dropzone
          disabled={disabled}
          disablePreview={disablePreview}
          style={{}}
          multiple={multiple}
          accept={acceptFileTypes}
          onDrop={this.onDrop}
          ref={this.dropzoneRef}>
          {children ? (
            children
          ) : (
            <>
              {!allowUpload && <Box>{getI18n().t('ui:fileUploadLimitReached')}</Box>}
              {allowUpload && (
                <>
                  <Box style={styles.content}>
                    <Box style={styles.plusIcon}>
                      <Icon icon={Plus} size={14} />
                    </Box>
                    <Text className="md:typo-base typo-sm text-indigo-crush">{uploadLabel}</Text>
                    {showUploadInfo && (
                      <Box style={styles.uploadInfo}>
                        <Trans ns="ui" i18nKey="fileFormat">
                          <span>
                            File format: <strong>.JPG</strong>, <strong>.PNG</strong> or{' '}
                            <strong>.WEBP</strong> | Minimum width: <strong>600 pixels</strong> |
                            Max file size: <strong>20mb</strong>
                          </span>
                        </Trans>
                      </Box>
                    )}
                  </Box>
                  <UploadingFile
                    droppedFiles={droppedFiles}
                    errors={errors}
                    uploadType={uploadType}
                    multiple={multiple}
                    disablePreview={disablePreview}
                  />
                </>
              )}
            </>
          )}
        </Dropzone>
      </Box>
    );
  }
}

export default FileUploader;
